import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Vehicle API
export const getVehicleMakes = () => api.get('/vehicles/makes');
export const getVehicleModels = (make) => api.get(`/vehicles/models/${encodeURIComponent(make)}`);
export const getVehicleYears = () => api.get('/vehicles/years');
export const getVehicleColors = () => api.get('/vehicles/colors');

// Services API
export const getServices = () => api.get('/services');

// Booking API
export const createBooking = (data) => api.post('/bookings', data);
export const getBooking = (id) => api.get(`/bookings/${id}`);

// Payment API
export const createCheckoutSession = (bookingId, originUrl) => 
  api.post('/payments/checkout', { booking_id: bookingId, origin_url: originUrl });
export const getPaymentStatus = (sessionId) => api.get(`/payments/status/${sessionId}`);

// Admin API
export const adminLogin = (email, password) => api.post('/admin/login', { email, password });
export const forgotPassword = (email) => api.post('/admin/forgot-password', { email });
export const getAdminBookings = () => api.get('/admin/bookings');
export const getAdminStats = () => api.get('/admin/stats');
export const updateBookingStatus = (bookingId, status) => 
  api.patch(`/admin/bookings/${bookingId}/status?status=${status}`);

// Trash (soft delete) API
export const deleteBooking = (bookingId) => api.delete(`/admin/bookings/${bookingId}`);
export const getTrashedBookings = () => api.get('/admin/bookings/trash');
export const restoreBooking = (bookingId) => api.post(`/admin/bookings/${bookingId}/restore`);
export const permanentDeleteBooking = (bookingId) => api.delete(`/admin/bookings/${bookingId}/permanent`);

// Admin Create Booking
export const adminCreateBooking = (data) => api.post('/admin/bookings/create', data);

export const sendEmail = (recipientEmail, subject, htmlContent, files = []) => {
  const formData = new FormData();
  formData.append('recipient_email', recipientEmail);
  formData.append('subject', subject);
  formData.append('html_content', htmlContent);
  files.forEach(f => formData.append('files', f));
  return api.post('/emails/send', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Admin Email API
export const getSentEmails = () => api.get('/admin/emails/sent');
export const getReceivedEmails = () => api.get('/admin/emails/received');
export const markEmailRead = (emailId) => api.patch(`/admin/emails/received/${emailId}/read`);
export const markEmailUnread = (emailId) => api.patch(`/admin/emails/received/${emailId}/unread`);
export const toggleStarReceived = (emailId) => api.patch(`/admin/emails/received/${emailId}/star`);
export const toggleStarSent = (emailId) => api.patch(`/admin/emails/sent/${emailId}/star`);
export const deleteReceivedEmail = (emailId) => api.delete(`/admin/emails/received/${emailId}`);
export const deleteSentEmail = (emailId) => api.delete(`/admin/emails/sent/${emailId}`);
export const bulkDeleteReceived = (emailIds) => api.post('/admin/emails/received/bulk-delete', { email_ids: emailIds });
export const bulkDeleteSent = (emailIds) => api.post('/admin/emails/sent/bulk-delete', { email_ids: emailIds });
export const bulkMarkRead = (emailIds) => api.post('/admin/emails/received/bulk-read', { email_ids: emailIds });
export const retryAttachments = (emailId) => api.post(`/admin/emails/received/${emailId}/retry-attachments`);

// Email Trash API
export const getTrashedEmails = () => api.get('/admin/emails/trash');
export const restoreEmail = (emailId, type) => api.post(`/admin/emails/${emailId}/restore`, { type });
export const permanentDeleteEmail = (emailId, type) => api.delete(`/admin/emails/${emailId}/permanent?type=${type}`);

// Admin Analytics API
export const getSalesAnalytics = () => api.get('/admin/analytics/sales');
export const getTrafficAnalytics = () => api.get('/admin/analytics/traffic');

// Page View Tracking
export const trackPageView = (page, referrer) => 
  api.post('/track/pageview', { page, referrer }).catch(() => {});

// Session Heartbeat
export const trackHeartbeat = (sessionId) =>
  api.post('/track/heartbeat', { session_id: sessionId }).catch(() => {});
